import { useMemo } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Chip } from '@weareopenr/component-library';
import { DataTable } from '../../components/DataTable/DataTable';
import { TenantBilling } from './TenantBilling';
import { TenantContacts } from './TenantContacts';
import { TenantProvisioning } from './TenantProvisioning';
import { TenantInvoices } from './TenantInvoices';
import { useTenant, useTenantAudit, useTenantExtensions, useTenantUsers } from '../../hooks';
import type { IAuditEntry, ITenantExtension, ITenantSummary, ITenantUser } from '../../types';
import {
  HEALTH_LABEL,
  STATUS_COLOUR,
  formatDate,
  formatMoney,
  initialsOf,
} from '../Tenants/Tenants';
import styles from './Tenant.module.scss';

const TABS = [
  { id: 'summary', label: 'Summary' },
  { id: 'users', label: 'Users' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'extensions', label: 'Extensions' },
  { id: 'billing', label: 'Billing' },
  { id: 'invoices', label: 'Invoices' },
  { id: 'provisioning', label: 'Provisioning' },
  { id: 'audit', label: 'Audit' },
];

const Loading = ({ what }: { what: string }) => <p className={styles.muted}>Loading {what}…</p>;

const Failed = ({ error }: { error: unknown }) => (
  <p className={styles.error}>
    Could not load: {(error as { message?: string })?.message ?? 'unknown error'}
  </p>
);

const Stat = ({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) => (
  <div className={styles.stat}>
    <span className={styles.statLabel}>{label}</span>
    <span className={styles.statValue}>{value}</span>
    {hint ? <span className={styles.statHint}>{hint}</span> : null}
  </div>
);

const Summary = ({ tenant }: { tenant: ITenantSummary }) => (
  <div className={styles.summary}>
    <div className={styles.stats}>
      <Stat label="Users" value={tenant.users ?? '—'} hint="WorkOS members" />
      <Stat label="Brands" value={tenant.brands ?? '—'} />
      <Stat label="Venues" value={(tenant.venues ?? 0).toLocaleString('en-GB')} />
      <Stat
        label="Monthly recurring"
        value={formatMoney(tenant.mrr)}
        hint={tenant.base_plan ?? 'no base plan'}
      />
      <Stat label="Last publish" value={formatDate(tenant.last_publish_at)} />
      <Stat label="Failures, 24h" value={tenant.failed_events_24h ?? '—'} />
    </div>
    <section className={styles.panel}>
      <h2 className={styles.panelTitle}>Identifiers</h2>
      <dl className={styles.dl}>
        <dt>Tenant key</dt>
        <dd>
          <code>{tenant.name}</code>
        </dd>
        <dt>Schema</dt>
        <dd>
          <code>{tenant.name}</code>
        </dd>
        <dt>Secret</dt>
        <dd>
          <code>tenant_{tenant.name}_config</code>{' '}
          {tenant.secret_present ? (
            ''
          ) : (
            <span className={styles.warn}>missing in this environment</span>
          )}
        </dd>
        <dt>WorkOS organisation</dt>
        <dd>
          {tenant.workos_org_id ? (
            <code>{tenant.workos_org_id}</code>
          ) : (
            <span className={styles.muted}>not matched</span>
          )}
        </dd>
        <dt>Environment</dt>
        <dd>{tenant.environment}</dd>
        <dt>Notes</dt>
        <dd>{tenant.notes ?? <span className={styles.muted}>none</span>}</dd>
      </dl>
      {tenant.stats_error ? (
        <p className={styles.warn}>Stats unavailable: {tenant.stats_error}</p>
      ) : null}
    </section>
  </div>
);

const Users = ({ name }: { name: string }) => {
  const { data, isLoading, error } = useTenantUsers(name);
  const columns = useMemo<ColumnDef<ITenantUser, any>[]>(
    () => [
      {
        id: 'user',
        header: 'User',
        accessorFn: (row) => `${row.first_name ?? ''} ${row.last_name ?? ''} ${row.email}`,
        size: 280,
        cell: ({ row }) => {
          const full = [row.original.first_name, row.original.last_name].filter(Boolean).join(' ');
          return (
            <div className={styles.user}>
              <span className={styles.userAvatar} aria-hidden="true">
                {initialsOf(full || row.original.email)}
              </span>
              <div className={styles.userText}>
                <span className={styles.userName}>{full || row.original.email}</span>
                {full ? <span className={styles.userEmail}>{row.original.email}</span> : null}
              </div>
            </div>
          );
        },
      },
      {
        header: 'Role',
        accessorKey: 'role',
        size: 110,
        cell: ({ getValue }) =>
          getValue<string | null>() ?? <span className={styles.muted}>none</span>,
      },
      {
        header: 'Status',
        accessorKey: 'status',
        size: 110,
        cell: ({ getValue }) => (
          <Chip
            label={getValue<string>()}
            backgroundColour={getValue<string>() === 'active' ? 'kale' : 'liquorice'}
          />
        ),
      },
      {
        header: 'Verified',
        accessorKey: 'email_verified',
        size: 100,
        cell: ({ getValue }) => (getValue<boolean>() ? 'Yes' : 'No'),
      },
      {
        header: 'Last sign in',
        accessorKey: 'last_sign_in_at',
        size: 130,
        cell: ({ getValue }) => formatDate(getValue<string | null>()),
      },
      {
        header: 'Joined',
        accessorKey: 'created_at',
        size: 120,
        cell: ({ getValue }) => formatDate(getValue<string>()),
      },
    ],
    [],
  );
  if (isLoading) return <Loading what="users" />;
  if (error || !data) return <Failed error={error} />;
  return (
    <>
      <p className={styles.muted}>
        {data.organisation_id ? (
          <>
            {data.users.length} members of WorkOS organisation <code>{data.organisation_id}</code>
          </>
        ) : (
          'No WorkOS organisation matches this tenant name in this environment.'
        )}
      </p>
      <DataTable
        columns={columns}
        data={data.users}
        storageKey="nexus-ui.tenant.users"
        emptyMessage="No users."
      />
    </>
  );
};

const Extensions = ({ name }: { name: string }) => {
  const { data, isLoading, error } = useTenantExtensions(name);
  const columns = useMemo<ColumnDef<ITenantExtension, any>[]>(
    () => [
      { header: 'Brand', accessorKey: 'brand_name', size: 220 },
      { header: 'Add-on', accessorKey: 'addon_name', size: 220 },
      {
        header: 'Code',
        accessorKey: 'addon_code',
        size: 120,
        cell: ({ getValue }) => <code>{getValue<string | null>() ?? '—'}</code>,
      },
      {
        header: 'Instances',
        accessorKey: 'instances',
        enableSorting: false,
        cell: ({ getValue }) => {
          const instances = getValue<ITenantExtension['instances']>();
          return instances.length === 0 ? (
            <span className={styles.muted}>none</span>
          ) : (
            instances.map((instance) => instance.name).join(', ')
          );
        },
      },
      {
        header: 'Installed',
        accessorKey: 'created_at',
        size: 120,
        cell: ({ getValue }) => formatDate(getValue<string>()),
      },
    ],
    [],
  );
  if (isLoading) return <Loading what="extensions" />;
  if (error || !data) return <Failed error={error} />;
  return (
    <DataTable
      columns={columns}
      data={data}
      storageKey="nexus-ui.tenant.extensions"
      emptyMessage="No add-ons installed."
    />
  );
};

const Audit = ({ name }: { name: string }) => {
  const { data, isLoading, error } = useTenantAudit(name);
  const columns = useMemo<ColumnDef<IAuditEntry, any>[]>(
    () => [
      {
        header: 'When',
        accessorKey: 'at',
        size: 170,
        cell: ({ getValue }) => new Date(getValue<string>()).toLocaleString('en-GB'),
      },
      { header: 'Who', accessorKey: 'staff_email', size: 220 },
      {
        header: 'Action',
        accessorKey: 'action',
        size: 180,
        cell: ({ getValue }) => <code>{getValue<string>()}</code>,
      },
      {
        header: 'Target',
        accessorKey: 'target_id',
        size: 220,
        cell: ({ row }) => `${row.original.target_type} ${row.original.target_id}`,
      },
    ],
    [],
  );
  if (isLoading) return <Loading what="audit" />;
  if (error || !data) return <Failed error={error} />;
  return (
    <DataTable
      columns={columns}
      data={data}
      storageKey="nexus-ui.tenant.audit"
      emptyMessage="No staff actions recorded for this tenant."
    />
  );
};

export const Tenant = () => {
  const { tenantId = '' } = useParams();
  const { data: tenant, isLoading, error } = useTenant(tenantId);

  return (
    <div className={styles.page}>
      <nav className={styles.crumbs} aria-label="Breadcrumb">
        <Link to="/tenants">Tenants</Link>
        <span aria-hidden="true">/</span>
        <span>{tenant?.display_name ?? tenantId}</span>
      </nav>
      <header className={styles.header}>
        <span className={styles.bigAvatar} aria-hidden="true">
          {initialsOf(tenant?.display_name ?? tenantId)}
        </span>
        <div className={styles.headerText}>
          <h1 className={styles.title}>{tenant?.display_name ?? tenantId}</h1>
          <div className={styles.headerMeta}>
            {tenant ? (
              <Chip label={tenant.status} backgroundColour={STATUS_COLOUR[tenant.status]} />
            ) : null}
            {tenant ? (
              <span className={styles.health} data-health={tenant.health}>
                <span className={styles.dot} />
                {HEALTH_LABEL[tenant.health]}
              </span>
            ) : null}
            <code className={styles.key}>{tenantId}</code>
          </div>
        </div>
      </header>
      <div className={styles.tabs} role="tablist">
        {TABS.map((tab) => (
          <NavLink
            key={tab.id}
            to={`/tenants/${encodeURIComponent(tenantId)}/${tab.id}`}
            className={({ isActive }) => (isActive ? styles.tabOn : styles.tab)}
            role="tab"
          >
            {tab.label}
          </NavLink>
        ))}
      </div>
      <div className={styles.body}>
        {isLoading ? <Loading what="tenant" /> : null}
        {error ? <Failed error={error} /> : null}
        {tenant ? (
          <Routes>
            <Route
              index
              element={
                <Navigate
                  to={`/tenants/${encodeURIComponent(tenantId)}/${
                    tenant.status === 'provisioning' || tenant.status === 'failed'
                      ? 'provisioning'
                      : 'summary'
                  }`}
                  replace
                />
              }
            />
            <Route path="summary" element={<Summary tenant={tenant} />} />
            <Route path="users" element={<Users name={tenantId} />} />
            <Route path="extensions" element={<Extensions name={tenantId} />} />
            <Route path="contacts" element={<TenantContacts tenant={tenant} />} />
            <Route path="billing" element={<TenantBilling tenant={tenant} />} />
            <Route path="invoices" element={<TenantInvoices tenant={tenant} />} />
            <Route path="provisioning" element={<TenantProvisioning tenant={tenant} />} />
            <Route path="audit" element={<Audit name={tenantId} />} />
          </Routes>
        ) : null}
      </div>
    </div>
  );
};
